import { getIdToken } from './liff';
import type {
  PreorderWithItems,
  PreorderListResponse,
  PreorderListParams,
  CreatePreorderRequest,
  UpdatePreorderRequest,
  AuthTokenResponse,
  User,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

let accessToken: string | null = null;
let currentUser: User | null = null;

/**
 * HTTP client for API calls
 */
async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
    throw new Error(error.error?.message || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Initialize API client - exchange LIFF token for API token
 */
export async function initApi(): Promise<User | null> {
  const idToken = getIdToken();
  if (!idToken) {
    console.warn('No LIFF ID token available');
    return null;
  }

  try {
    const result = await request<AuthTokenResponse>('/auth/token', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });

    accessToken = result.accessToken;
    currentUser = result.user;

    console.log('API authenticated', { userId: currentUser.id });

    return currentUser;
  } catch (error) {
    console.error('Failed to authenticate API:', error);
    return null;
  }
}

/**
 * Get current authenticated user
 */
export function getUser(): User | null {
  return currentUser;
}

/**
 * Check if API is authenticated
 */
export function isAuthenticated(): boolean {
  return accessToken !== null;
}

// ==================== Preorder API ====================

/**
 * List preorders
 */
export async function listPreorders(
  params: PreorderListParams = {}
): Promise<PreorderListResponse> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.receiveStatus) searchParams.set('receiveStatus', params.receiveStatus);
  if (params.paymentStatus) searchParams.set('paymentStatus', params.paymentStatus);
  if (params.search) searchParams.set('search', params.search);
  if (params.overdue) searchParams.set('overdue', 'true');

  const query = searchParams.toString();
  const path = query ? `/api/preorders?${query}` : '/api/preorders';

  return request<PreorderListResponse>(path);
}

/**
 * Get single preorder
 */
export async function getPreorder(id: string): Promise<{ preorder: PreorderWithItems }> {
  return request<{ preorder: PreorderWithItems }>(`/api/preorders/${id}`);
}

/**
 * Create preorder manually
 */
export async function createPreorder(
  data: CreatePreorderRequest
): Promise<{ preorder: PreorderWithItems }> {
  return request<{ preorder: PreorderWithItems }>('/api/preorders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update preorder
 */
export async function updatePreorder(
  id: string,
  data: UpdatePreorderRequest
): Promise<{ preorder: PreorderWithItems }> {
  return request<{ preorder: PreorderWithItems }>(`/api/preorders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete preorder
 */
export async function deletePreorder(id: string): Promise<void> {
  await request<{ success: boolean }>(`/api/preorders/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Mark preorder as received
 */
export async function markReceived(id: string): Promise<{ preorder: PreorderWithItems }> {
  return request<{ preorder: PreorderWithItems }>(`/api/preorders/${id}/receive`, {
    method: 'POST',
  });
}

/**
 * Unmark preorder as received
 */
export async function unmarkReceived(id: string): Promise<{ preorder: PreorderWithItems }> {
  return request<{ preorder: PreorderWithItems }>(`/api/preorders/${id}/receive`, {
    method: 'DELETE',
  });
}

// ==================== User API ====================

/**
 * Get user quota
 */
export async function getUserQuota(): Promise<{
  quota: { month: string; usedCount: number; maxCount: number };
}> {
  return request<{ quota: { month: string; usedCount: number; maxCount: number } }>(
    '/api/users/me/quota'
  );
}
